<template>
    <!-- *********************************************
    *                  Credit Block                  *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="coin-drop"
    :applyBorder="false"
    :title="t('credits.myCredits.amortization.step1.amortizationAccountTitle')"/>
    <text-block-generic
    :params="amortizationAccountParams"
    :mask="ACCOUNTS_NIB_IBAN"/>
    <container-collapsible
    startClosed
    doubleColumn
    :title="t('textBlocks.showDetails')"
    :openTitle="t('textBlocks.hideDetails')">
        <text-block-generic
        v-for="entry in amortizationAccountEntries"
        :key="entry"
        :params="entry"
        :applyEllipsis="entry.applyEllipsis"
        :mask="entry.mask"/>
    </container-collapsible>
    <!-- *********************************************
    *                  Account Block                 *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="account-balance"
    :title="t('credits.myCredits.amortization.step1.amortizationAccountOriginTitle')"/>
    <dropdown-account
    id="accountId"
    :params="accountDropdownParams"
    :options="accountsBalances"
    :selectedOption="accountFormData.accountId"
    @clicked="emit('updateData', { fieldName: 'accountId', inputValue: $event })"/>
    <!-- *********************************************
    *                   Amount Block                 *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="money"
    :title="t('credits.myCredits.amortization.step1.amortizationAmountTitle')"/>
    <!-- Amortization Type -->
    <selection-controller-radio-group
    :label="t('credits.myCredits.amortization.step1.amortizationType')"
    :radioParams="radioParamsData"
    @clicked="emit('updateData', { fieldName: 'amortizationType', inputValue: radioParamsData.find(ele => ele.id === $event).id})"/>
    <!-- Output if type is partial and only 1 installment to amortize -->
    <text-block-generic
    v-if="accountFormData.amortizationType === 'partial' && !partialMultipleInstallments"
    :params="numberOfSettlementsBlockParams"/>
    <!-- Output if type is partial and more than 1 installment to amortize -->
    <dropdown-generic
    v-if="accountFormData.amortizationType === 'partial' && partialMultipleInstallments"
    :params="numberOfSettlementsParams"
    :options="numberOfSettlementsOptions"
    :selectedOption="accountFormData.numberOfSettlements"
    @clicked="emit('updateData', { fieldName: 'numberOfSettlements', inputValue: $event})"/>
    <!-- Amortization Values -->
    <text-block-generic :params="capitalAmountParams"/>
    <text-block-generic :params="feesParams"/>
    <divider-generic dividerSize="medium" :style="{'padding-top': 0}"/>
    <text-block-generic class="amortization-amount" :params="amortizationAmountParams"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ContainerCollapsible from '@/components/generic/containers/Collapsible'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import SelectionControllerRadioGroup from '@/components/generic/selectors/radio/RadioGroup'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'

/* Vue & Assets */
import { ref, computed, onMounted } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { ACCOUNTS_NIB_IBAN } from '@/assets/constants/masks'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['updateData', 'scrollTo'])

/* props received
@accountFormData {Object} object with info for our current form
@creditInfo {Object} - object with amortization amounts
@accountsBalances {Array} - array with valid origin accounts
@financialPlan {Object} - object with financialPlan
*/
const props = defineProps({
	accountFormData: Object,
	creditInfo: Object,
	accountsBalances: Array,
    financialPlan: Array
})

const amortizationAccountParams = computed(() => {
	return {
		label: t('credits.myCredits.amortization.step1.account'),
		content: props.accountFormData.accountNumber
	}
})

const amortizationAccountEntries = ref([
	{
		label: t('credits.myCredits.amortization.step1.showDetails.creditAmount'),
		content: amountFormat(props.accountFormData.creditAmount, props.accountFormData.currency)
	}, {
		label: t('credits.myCredits.amortization.step1.showDetails.debtAmount'),
		content: amountFormat(props.accountFormData.debtAmount, props.accountFormData.currency)
	}
])

const accountDropdownParams = ref({
	labelText: t('credits.myCredits.amortization.step1.account'),
	mandatory: true,
	errorState: false,
	errorMessage: null
})

const radioParamsData = computed(() => {
    return [{
        id: 'total',
        text: t('credits.myCredits.amortization.step1.total'),
        value: true,
        selected: props.accountFormData.amortizationType === 'total'
    }, {
        id: 'partial',
        text: t('credits.myCredits.amortization.step1.partial'),
        value: false,
        selected: props.accountFormData.amortizationType === 'partial',
        disabled: props.accountFormData.numberOfRemainingInstallments === 1 || !props.accountFormData.numberOfRemainingInstallments
    }]
})

const partialMultipleInstallments = computed(() => {
	return (props.accountFormData.numberOfRemainingInstallments - 1) > 1
})

const numberOfSettlementsBlockParams = computed(() => {
    return {
        label: t('credits.myCredits.amortization.step1.settlementNumber'),
        content: props.accountFormData.numberOfSettlements
    }
})

const numberOfSettlementsParams = ref({
	labelText: t('credits.myCredits.amortization.step1.settlementNumber')
})

const numberOfSettlementsOptions = computed(() => {
    const settlementsArray = Array.from(Array(props.accountFormData.numberOfRemainingInstallments).keys())

    return settlementsArray.map(number => {
        return {
            id: number + 1,
            text: number + 1
        }
    })
})

const capitalAmountParams = computed(() => {
    const amountCalc = props.accountFormData.amortizationType === 'partial' ? props.accountFormData.numberOfSettlements * props.financialPlan[0].capitalDebt : props.accountFormData.debtAmount

    return {
        label: t('credits.myCredits.amortization.step1.capitalAmount'),
        content: amountFormat(amountCalc, props.accountFormData.currency)
    }
})

const feesParams = computed(() => {
    const feesCalc = (props.accountFormData.amortizationType === 'partial' ? props.accountFormData.numberOfSettlements : props.accountFormData.numberOfRemainingInstallments) * props.accountFormData.loanInterestRate

    return {
        label: t('credits.myCredits.amortization.step1.fees'),
        content: amountFormat(feesCalc, props.accountFormData.currency)
    }
})

const amortizationAmountParams = computed(() => {
    const amountCalc = props.accountFormData.amortizationType === 'partial' ? props.accountFormData.numberOfSettlements * props.financialPlan[0].capitalDebt : props.accountFormData.debtAmount

    const feesCalc = (props.accountFormData.amortizationType === 'partial' ? props.accountFormData.numberOfSettlements : props.accountFormData.numberOfRemainingInstallments) * props.accountFormData.loanInterestRate

    const totalAmount = amountCalc + feesCalc

	return {
		label: t('credits.myCredits.amortization.step1.amortizationAmount'),
		content: amountFormat(totalAmount, props.accountFormData.currency)
	}
})

onMounted(() => {
    emit('scrollTo', 0)
})
</script>

<style lang="scss" scoped>
    .amortization-amount {
        :deep(.content-wrapper) {
            .content {
                @include H3-Regular;
                color: var(--secondary-dark-3);
            }
        }
    }
</style>
